<template>
  <div class="genres-management">
    <div class="title">
      {{ label }}
      <span
        v-if="required"
        class="asterisk"
      >
        *
      </span>
    </div>
    <div
      :class="[commonError.length ? 'common-error' : '',
               isNotValidValue ? 'not-valid' : '']"
    >
      <MultiSelect
        v-model="selectedValue"
        :options="options"
        :placeholder="placeholder"
        :selectLabel="selectLabel"
        :selectedLabel="selectedLabel"
        :deselectLabel="deselectLabel"
        label="genres"
        trackBy="id"
        :multiple="true"
        :taggable="false"
        :blockKeys="['Delete']"
        :searchable="searchable"
        :disabled="disabled"
        :hideSelected="hideSelected"
        :optionsLimit="max"
        :customLabel="customLabel"
        @search-change="onSearchChange"
        @input="onInput"
      >
        <template
          slot="tag"
          slot-scope="props"
        >
          <span
            v-if="genres.length > 0"
            :class="selectedGenreCalasses(props.option)"
            :title="selectedGenreTitle(props.option)"
          >
            <span>
              {{ customLabel(props.option) }}
            </span>
            <i
              aria-hidden="true"
              tabindex="1"
              class="multiselect__tag-icon"
              @click="props.remove(props.option)"
            >
            </i>
          </span>
          <span v-else></span>
        </template>
      </MultiSelect>
      <span
        v-if="commonError.length"
        class="error-label"
      >
        {{ commonError }}
      </span>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import MultiSelect from 'vue-multiselect';
import { GET_GENRES_FOR_CHOICE_REQUEST } from '../../store/actions/genres/genresActions';

export default {
  name: 'GenresManagement',
  components: {
    MultiSelect,
  },
  props: {
    resetValidation: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    label: {
      type: String,
      default: 'Select Genres',
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    searchable: {
      type: Boolean,
      default: true,
      required: false,
    },
    selectedValue: {
      type: Array,
      default: () => [],
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    hideSelected: {
      type: Boolean,
      default: false,
      required: false,
    },
    max: {
      type: Number,
      default: 1000,
    },
    selectLabel: {
      type: String,
      default: '',
    },
    selectedLabel: {
      type: String,
      default: '',
    },
    deselectLabel: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
      required: false,
    },
    commonError: {
      type: String,
      default: '',
    },
    inheritedGenres: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      value: [],
      genres: [],
      isNotValidValue: false,
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccountId',
    ]),
    options() {
      return this.genres.map((genre) => ({
        id: genre.id,
      }));
    },
  },
  created() {
    this.getGenres();
  },
  methods: {
    getGenres() {
      const params = {
        accountId: this.getCurrentAccountId,
      };
      this.$store.dispatch(GET_GENRES_FOR_CHOICE_REQUEST, params).then((res) => {
        this.genres = res.data;
      });
    },
    onSearchChange(searchQuery) {
      this.resetValidation();

      const validation = this.validateSearch(searchQuery.trim());

      this.isNotValidValue = !validation.isValid;
    },
    validateSearch(searchValue) {
      return {
        value: searchValue,
        isValid: true,
        message: '',
      };
    },
    onInput(value) {
      this.resetValidation();

      if (this.inheritedGenres.length > 0) {
        this.inheritedGenres.forEach((inheritedGenre) => {
          if (!value.some((genre) => genre.id === inheritedGenre.id)) {
            value.push(inheritedGenre);
          }
        });
      }

      this.onChange(value);
    },
    customLabel(option, withDisabled = false) {
      const genre = this.genres.find((genre) => genre.id === option.id);
      let label = genre?.name || '';

      if (withDisabled && !genre?.enabled) {
        label = `${label} (disabled)`;
      }

      if (withDisabled && this.isGenreInherited(option)) {
        label = `${label} (inherited)`;
      }

      return label;
    },
    selectedGenreCalasses(option) {
      const genre = this.genres.find((item) => item.id === option.id);
      return {
        multiselect__tag: true,
        'multiselect__tag--disabled': !genre?.enabled,
        'multiselect__tag--inherited': this.isGenreInherited(option),
      };
    },
    selectedGenreTitle(option) {
      const genre = this.genres.find((item) => item.id === option.id);

      if (!genre?.enabled) {
        return 'This Genre is disabled';
      }
      if (this.isGenreInherited(option)) {
        return 'This Genre is inherited';
      }

      return '';
    },
    isGenreInherited(genre) {
      return this.inheritedGenres.some((inheritedGenre) => inheritedGenre.id === genre.id);
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped lang="scss">
.genres-management {
  .title {
    font-size: 16px;
    margin-bottom: 10px;
    color: var(--white);
    font-weight: normal;
  }

  .multiselect {
    box-sizing: border-box;
  }

  .multiselect__tags {
    background: #222 !important;
    border-color: transparent;
    border-radius: 4px;
  }

  .multiselect__tag {
    background: #e77925;
    cursor: pointer;

    &--disabled {
      background: #4c5057;

      .multiselect__tag-icon:focus,
      .multiselect__tag-icon:hover {
        background: #ff6a6a;
      }
    }

    &--inherited {
      background: #4c5057;
      cursor: not-allowed;
      padding-right: 10px;

      .multiselect__tag-icon:focus,
      .multiselect__tag-icon:hover {
        background: #4c5057;
      }

      i {
        display: none;
      }
    }
  }

  .multiselect__tag-icon:focus,
  .multiselect__tag-icon:hover {
    background: var(--darkorange);
  }

  .asterisk {
    color: #fb3951;
  }
}
</style>
